import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { 
  signInWithEmailAndPassword,
  signOut,
  User,
  onAuthStateChanged,
  updatePassword as firebaseUpdatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  setPersistence,
  browserLocalPersistence,
  getAuth
} from 'firebase/auth'
import { doc, getDoc, collection, query, where, getDocs, updateDoc, setDoc } from 'firebase/firestore'
import { auth, db } from '../firebase/config'
import { useCurrentOrganizationStore } from './currentOrganization'
import { useProfileStore } from './profile'
import { userOrganizationService } from '@/firebase/services/userOrganization.service'
import { verifyTOTP } from '../utils/totp'

interface UserData {
  role: string
  name: string
  profileId?: string
  email: string
  phone?: string
  position?: string
  twoFactorEnabled?: boolean
  twoFactorSecret?: string
  active: boolean
}

interface Profile {
  id: string
  name: string
  menuPermissions: Record<string, boolean>
}

interface UserOrganization {
  organizationId: string
  role: string
}

interface UserProfile {
  name: string
  email: string
  phone?: string
  position?: string
}

interface PasswordUpdate {
  currentPassword: string
  newPassword: string
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const userData = ref<UserData | null>(null)
  const userProfile = ref<Profile | null>(null)
  const userOrganizations = ref<UserOrganization[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const pendingVerification = ref(false)

  // Computed properties
  const isAuthenticated = computed(() => !!user.value)
  const isAdmin = computed(() => userData.value?.role === 'admin')
  const isManager = computed(() => userData.value?.role === 'manager')
  const isUser = computed(() => userData.value?.role === 'user')
  const currentUser = computed(() => user.value)
  
  const currentOrganizationId = computed(() => {
    const currentOrganizationStore = useCurrentOrganizationStore()
    return currentOrganizationStore.organizationId
  })

  const currentOrganization = computed(() => {
    const currentOrganizationStore = useCurrentOrganizationStore()
    return currentOrganizationStore.currentOrganization
  })

  const hasPageAccess = (pageName: string) => {
    // Verifica se é admin global
    if (userData.value?.role === 'admin') return true
    
    // Verifica se é admin na organização atual
    const currentOrgId = localStorage.getItem('currentOrganizationId')
    if (currentOrgId) {
      const orgAccess = userOrganizations.value.find(org => org.organizationId === currentOrgId)
      if (orgAccess?.role === 'admin') return true
    }
    
    // Se não tem perfil, não tem acesso
    if (!userProfile.value) return false
    
    // Verifica se tem permissão no perfil para a página
    // Adiciona a barra no início se não existir
    const path = pageName.startsWith('/') ? pageName : `/${pageName}`
    return userProfile.value.menuPermissions?.[path] || false
  }

  const hasOrganizationAccess = (organizationId: string) => {
    // Admin universal tem acesso a tudo
    if (user.value?.uid === 'ZPVelJ5hXXV6rMmYdNxgVOwSCHA2') {
      return true
    }
    
    // Para outros usuários, verifica nas organizações do usuário
    return userOrganizations.value.some(org => org.organizationId === organizationId)
  }

  const setError = (message: string | null) => {
    error.value = message
  }

  const loadUserData = async (userId: string) => {
    try {
      console.log('[Auth Store] Carregando dados do usuário:', userId)
      
      // Tentar carregar dados do usuário várias vezes se necessário
      let attempts = 0;
      let userDoc = null;
      
      while (attempts < 3) {
        try {
          userDoc = await getDoc(doc(db, 'users', userId))
          if (userDoc.exists()) {
            break;
          }
          attempts++;
          await new Promise(resolve => setTimeout(resolve, 1000)); // esperar 1 segundo entre tentativas
        } catch (error) {
          console.error(`[Auth Store] Tentativa ${attempts + 1} falhou:`, error)
          attempts++;
          if (attempts < 3) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }

      if (!userDoc || !userDoc.exists()) {
        console.error('[Auth Store] Não foi possível carregar dados do usuário após várias tentativas')
        userData.value = null
        userProfile.value = null
        userOrganizations.value = []
        throw new Error('Não foi possível carregar dados do usuário')
      }

      // Carregar dados básicos
      const data = userDoc.data() as UserData
      console.log('[Auth Store] Dados do usuário carregados:', {
        role: data.role,
        name: data.name,
        has2FA: data.twoFactorEnabled
      })
      userData.value = data

      // Se não estiver pendente de verificação 2FA, carregar dados adicionais
      if (!pendingVerification.value) {
        try {
          const profileStore = useProfileStore()
          await profileStore.fetchProfiles()
          userProfile.value = profileStore.profiles.find(p => p.id === data.profileId) || null
          
          userOrganizations.value = await userOrganizationService.getUserOrganizations(userId)
          
          console.log('[Auth Store] Dados completos carregados:', {
            profile: userProfile.value?.name,
            organizations: userOrganizations.value.length
          })
        } catch (error) {
          console.error('[Auth Store] Erro ao carregar dados adicionais:', error)
          userProfile.value = null
          userOrganizations.value = []
        }
      }
    } catch (error) {
      console.error('[Auth Store] Erro ao carregar dados do usuário:', error)
      userData.value = null
      userProfile.value = null
      userOrganizations.value = []
      throw error
    }
  }

  // Initialize auth state listener
  onAuthStateChanged(auth, async (newUser) => {
    console.log('[Auth Store] Estado de autenticação alterado:', {
      userId: newUser?.uid,
      pendingVerification: pendingVerification.value
    })

    try {
      if (newUser) {
        // Se estiver pendente de verificação 2FA, não carregar dados ainda
        if (pendingVerification.value) {
          console.log('[Auth Store] Aguardando verificação 2FA')
          user.value = newUser
          return
        }

        // Carregar dados do usuário
        await loadUserData(newUser.uid)
        user.value = newUser
      } else {
        // Limpar dados quando o usuário deslogar
        user.value = null
        userData.value = null
        userProfile.value = null
        userOrganizations.value = []
        pendingVerification.value = false
      }
    } catch (error) {
      console.error('[Auth Store] Erro ao carregar dados:', error)
      // Em caso de erro, fazer logout para garantir consistência
      await signOut(auth)
    } finally {
      loading.value = false
    }
  })

  const login = async (email: string, password: string) => {
    try {
      loading.value = true
      error.value = null
      pendingVerification.value = false
      
      console.log('[Auth Store] Iniciando login...')
      console.log('[Auth Store] Email:', email)
      console.log('[Auth Store] Auth instance:', auth)
      
      // Primeiro, vamos verificar se o usuário já está logado
      if (auth.currentUser) {
        console.log('[Auth Store] Usuário já está logado, fazendo logout primeiro')
        await signOut(auth)
      }
      
      try {
        // Tentar login com email/senha
        const userCredential = await signInWithEmailAndPassword(auth, email, password)
        console.log('[Auth Store] Login bem sucedido:', userCredential)
        
        if (!userCredential.user) {
          throw new Error('Usuário não encontrado')
        }
        
        // Carregar dados do usuário para verificar 2FA
        const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid))
        console.log('[Auth Store] Dados do usuário:', userDoc.data())
        
        if (!userDoc.exists()) {
          throw new Error('Dados do usuário não encontrados')
        }
        
        const userDataTemp = userDoc.data() as UserData

        // Verificar se o usuário tem 2FA habilitado
        if (userDataTemp?.twoFactorEnabled) {
          console.log('[Auth Store] 2FA está habilitado, requerendo verificação')
          pendingVerification.value = true
          userData.value = userDataTemp // Necessário para ter acesso ao segredo 2FA
          user.value = userCredential.user
          return { success: true, requires2FA: true, user: userCredential.user }
        }
        
        // Se não precisar de 2FA, carregar todos os dados e prosseguir
        await loadUserData(userCredential.user.uid)
        user.value = userCredential.user
        return { success: true, requires2FA: false, user: userCredential.user }
        
      } catch (e: any) {
        console.error('[Auth Store] Erro no login:', e)
        console.error('[Auth Store] Detalhes do erro:', {
          code: e.code,
          message: e.message,
          details: e.customData?._tokenResponse
        })
        
        // Limpar todos os dados
        user.value = null
        userData.value = null
        userProfile.value = null
        userOrganizations.value = []
        pendingVerification.value = false
        
        // Tratamento específico para erros comuns
        if (e.code === 'auth/too-many-requests') {
          error.value = 'Muitas tentativas de login. Por favor, aguarde alguns minutos antes de tentar novamente.'
        } else if (e.code === 'auth/invalid-credential') {
          error.value = 'Email ou senha incorretos.'
        } else if (e instanceof Error) {
          error.value = e.message
        }
        
        throw e
      }
    } catch (error) {
      console.error('[Auth Store] Erro no login:', error)
      // Limpar todos os dados
      user.value = null
      userData.value = null
      userProfile.value = null
      userOrganizations.value = []
      pendingVerification.value = false
      
      if (error instanceof Error) {
        error.value = error.message
      }
      throw error
    } finally {
      loading.value = false
    }
  }

  const verify2FA = async (code: string) => {
    try {
      console.log('[Auth] Iniciando verificação 2FA...')
      
      if (!user.value) {
        throw new Error('Usuário não autenticado')
      }
      
      // Recarregar dados do usuário se necessário
      if (!userData.value?.twoFactorSecret) {
        console.log('[Auth] Recarregando dados do usuário para 2FA...')
        await loadUserData(user.value.uid)
        
        if (!userData.value?.twoFactorSecret) {
          throw new Error('Dados de 2FA não encontrados')
        }
      }
      
      // Verificar o código
      const isValid = verifyTOTP(userData.value.twoFactorSecret, code)
      console.log('[Auth] Resultado da verificação:', isValid)
      
      if (!isValid) {
        throw new Error('Código inválido')
      }
      
      // Se a verificação foi bem sucedida, carregar todos os dados do usuário
      console.log('[Auth] Verificação bem sucedida, carregando dados completos...')
      await loadUserData(user.value.uid)
      
      // Resetar o estado de verificação pendente
      pendingVerification.value = false
      
      return { success: true }
    } catch (error) {
      console.error('[Auth] Erro na verificação:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Erro na verificação')
    }
  }

  const logout = async () => {
    try {
      loading.value = true;
      error.value = null;
      
      console.log('[Auth Store] Iniciando logout...');
      
      // Limpar dados do usuário antes do logout
      user.value = null;
      userData.value = null;
      userProfile.value = null;
      userOrganizations.value = [];
      pendingVerification.value = false;
      
      // Fazer logout do Firebase
      await signOut(auth);
      
      // Limpar outros stores
      const profileStore = useProfileStore();
      profileStore.$reset();
      
      const currentOrganizationStore = useCurrentOrganizationStore();
      currentOrganizationStore.$reset();
      
      console.log('[Auth Store] Logout completo');
    } catch (e) {
      console.error('[Auth Store] Erro no logout:', e);
      if (e instanceof Error) {
        error.value = e.message;
      }
    } finally {
      loading.value = false;
    }
  };

  const updateUserProfile = async (profile: UserProfile) => {
    try {
      if (!user.value) {
        throw new Error('Usuário não autenticado')
      }

      const userRef = doc(db, 'users', user.value.uid)
      await updateDoc(userRef, profile)
      await loadUserData(user.value.uid)
      
      return true
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Erro ao atualizar perfil'
      return false
    }
  }

  const updatePassword = async (data: PasswordUpdate) => {
    try {
      if (!user.value) {
        throw new Error('Usuário não autenticado')
      }

      // Reautenticar o usuário
      const credential = EmailAuthProvider.credential(
        user.value.email!,
        data.currentPassword
      )
      await reauthenticateWithCredential(user.value, credential)

      // Atualizar a senha
      await firebaseUpdatePassword(user.value, data.newPassword)
      
      return true
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Erro ao atualizar senha'
      return false
    }
  }

  const refreshUserData = async () => {
    if (user.value?.uid) {
      await loadUserData(user.value.uid)
    }
  }

  // Inicialização do store
  async function init() {
    try {
      loading.value = true
      console.log('[Auth Store] Iniciando autenticação...')
      
      // Verificar se já há um usuário autenticado
      const currentUser = auth.currentUser
      if (currentUser) {
        console.log('[Auth Store] Usuário já autenticado:', {
          userId: currentUser.uid,
          email: currentUser.email
        })
        user.value = currentUser
        await loadUserData(currentUser.uid)
      }
      
      return true
    } catch (error) {
      console.error('[Auth Store] Erro na inicialização:', error)
      return false
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    user,
    userData,
    userProfile,
    userOrganizations,
    loading,
    error,
    pendingVerification,

    // Computed
    isAuthenticated,
    isAdmin,
    isManager,
    isUser,
    currentUser,
    currentOrganizationId,
    currentOrganization,
    
    // Methods
    login,
    logout,
    verify2FA,
    updateUserProfile,
    updatePassword,
    hasPageAccess,
    hasOrganizationAccess,
    setError,
    refreshUserData,
    init
  }
})